'use client';

/**
 * Avatar button + dropdown menu — ported from ThemeMaker's
 * {@code lms-portal/src/components/shell/ProfileMenu.tsx}.
 *
 * <p>Used by every header style that shows a profile pill (controlled
 * by {@code header.showProfile} in shell config).
 *
 * <h3>Behaviour</h3>
 * <ul>
 *   <li>Click avatar → dropdown opens (right-aligned).</li>
 *   <li>Click outside or Escape → closes.</li>
 *   <li>Item click navigates and closes.</li>
 *   <li>Soft slide-in animation, theme-aware visuals.</li>
 * </ul>
 *
 * <h3>Default items</h3>
 * <ul>
 *   <li>Profile  → {@code profileHref} (only when {@code extraItems} is empty)</li>
 *   <li>Sign out → {@code signOutHref} (destructive style, always last)</li>
 * </ul>
 *
 * <p>Pass {@code extraItems} (from the operator-authored
 * {@code header.profileMenu}) to fully own the rows above Sign out —
 * once the tenant configures any row of their own, the built-in
 * Profile row steps aside so tenants aren't stuck with a duplicate.
 *
 * <p>Hrefs are pre-resolved STRINGS, not a function. ProfileMenu is a
 * client component; Next.js refuses to serialize functions across the
 * server→client boundary. The header server component resolves each
 * href via {@code tenantHref()} before passing.
 *
 * <h3>Positioning</h3>
 * The panel is {@code position: fixed} and portaled to {@code document.body}
 * — same reasoning as {@code MegaMenuLink}/{@code LanguageSwitcher}: the
 * header may sit inside an {@code overflow: hidden}/{@code auto} ancestor
 * that would clip an absolutely-positioned panel, and the {@code 'grid'}
 * variant in particular can be tall. Position is measured from the
 * trigger via {@code getBoundingClientRect()} and re-measured on open,
 * resize, scroll, and panel size changes (grid group count varies).
 *
 * <h3>Variants</h3>
 * {@code variant: 'list'} (default) is the classic single-column list.
 * {@code variant: 'grid'} renders {@code extraItems} as a wide icon-tile
 * launcher, with {@code kind: 'group'} entries as headed sections
 * (ungrouped links batch into a plain flat grid — grouping is
 * progressive, not required). Destructive rows (sign-out) always
 * render as a full-width footer row below the grid rather than as a
 * tile.
 *
 * <p>The entries themselves (everything except the identity card and
 * sign-out) render via the exported {@link ProfileMenuEntries} —
 * shared with the mobile drawer (see {@code MobileMenu} in
 * {@code shells/headers/index.tsx}) so both surfaces render the exact
 * same list/grid+groups layout for a given {@code profileMenuStyle}
 * instead of the drawer maintaining its own parallel logic.
 */
import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';

/** A single clickable row — the default shape, and what a
 *  {@link ProfileMenuGroupItem}'s {@code links} contain. */
export interface ProfileMenuLinkItem {
  kind?: 'link';
  label: string;
  /** Pre-resolved URL string. */
  href: string;
  /** Optional icon — pass any ReactNode (e.g. an SVG component). */
  icon?: ReactNode;
  /** Style as a destructive action (red text). Useful for sign-out. */
  destructive?: boolean;
  /** Visual divider rendered ABOVE this item. Only honored in the
   *  {@code 'list'} variant. */
  separatorBefore?: boolean;
}

/** A labelled cluster of links, rendered as its own headed tile
 *  section in the {@code 'grid'} variant. In the {@code 'list'}
 *  variant its {@code links} flatten inline and the heading is
 *  dropped — same convention as top-bar {@code MenuGroup} flattening. */
export interface ProfileMenuGroupItem {
  kind: 'group';
  label: string;
  /** Shown next to the group heading. Optional. */
  icon?: ReactNode;
  /** Tint override for this group's tiles + heading dot. Falls back
   *  to the auto-rotating palette when unset. */
  color?: string;
  links: ProfileMenuLinkItem[];
}

/** One entry passed via {@code extraItems}. A plain object with no
 *  {@code kind} (or {@code kind: 'link'}) is a link — existing flat
 *  lists work unchanged; mixing in {@code kind: 'group'} entries is
 *  purely additive. */
export type ProfileMenuItem = ProfileMenuLinkItem | ProfileMenuGroupItem;

export interface ProfileMenuProps {
  currentUser: { name?: string; initials?: string; avatar?: string; email?: string } | null;
  /** Pre-resolved profile href. */
  profileHref: string;
  /** Pre-resolved sign-out href. */
  signOutHref: string;
  /** Optional extra items between Profile and Sign out. Each href
   *  must be pre-resolved by the caller. */
  extraItems?: ProfileMenuItem[];
  locale?: string;
  /** Dark-chrome styling for the avatar button border. */
  inverse?: boolean;
  /** Panel layout. {@code 'list'} (default) is the classic single-column
   *  list; {@code 'grid'} renders {@code extraItems} as a wide icon-tile
   *  launcher, with {@code kind: 'group'} entries as headed sections —
   *  better suited once a tenant has many rows. */
  variant?: 'list' | 'grid';
}

// First tint follows the tenant's actual brand color so the primary
// group reads as "on-brand"; the rest are a fixed, tasteful set since
// there's no secondary/tertiary brand token to draw from. A group's
// own `color` (if set) always wins over this.
const GRID_TINTS = ['var(--color-brand-500, #6366f1)', '#d3860a', '#0d9668', '#e64c72', '#56607a'];

function tintFor(groupIndex: number, override?: string): string {
  return override || GRID_TINTS[groupIndex % GRID_TINTS.length]!;
}

// Tiles for links that aren't inside any group fall back to the
// tenant's own brand color — same default the first real group gets.
const UNGROUPED_TINT = 'var(--color-brand-500, #6366f1)';

type MenuSection =
  | { kind: 'links'; items: ProfileMenuLinkItem[] }
  | { kind: 'group'; group: ProfileMenuGroupItem };

/** Grid-variant layout: consecutive plain links batch into one
 *  unheaded tile grid; each {@code kind: 'group'} entry becomes its
 *  own headed section — in array order, so authors control layout by
 *  ordering entries rather than by matching label text. */
function partitionForGrid(items: ProfileMenuItem[]): MenuSection[] {
  const sections: MenuSection[] = [];
  for (const item of items) {
    if (item.kind === 'group') {
      sections.push({ kind: 'group', group: item });
      continue;
    }
    const last = sections[sections.length - 1];
    if (last?.kind === 'links') last.items.push(item);
    else sections.push({ kind: 'links', items: [item] });
  }
  return sections;
}

/** List-variant layout: a group's links flatten inline, heading
 *  dropped — the list style has no room for a second hierarchy level. */
function flattenForList(items: ProfileMenuItem[]): ProfileMenuLinkItem[] {
  const out: ProfileMenuLinkItem[] = [];
  for (const item of items) {
    if (item.kind === 'group') out.push(...item.links);
    else out.push(item);
  }
  return out;
}

export function ProfileMenu({
  currentUser,
  profileHref,
  signOutHref,
  extraItems = [],
  locale = 'en',
  inverse,
  variant = 'list',
}: ProfileMenuProps) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const menuId = useId();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Fixed-position + portal (see file doc comment) — measured from the
  // trigger and re-measured on resize/scroll/panel-size change so the
  // grid variant (height varies with group count) stays anchored.
  useEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const measure = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const panel = panelRef.current;
      const rect = trigger.getBoundingClientRect();
      const pw = panel?.offsetWidth || 240;
      const ph = panel?.offsetHeight || 200;
      const margin = 8;
      const viewportPad = 8;
      const spaceBelow = window.innerHeight - rect.bottom - viewportPad;
      const placeBelow = ph <= spaceBelow || spaceBelow >= rect.top - viewportPad;
      const top = placeBelow ? rect.bottom + margin : Math.max(viewportPad, rect.top - margin - ph);
      const left = Math.max(
        viewportPad,
        Math.min(rect.right - pw, window.innerWidth - pw - viewportPad),
      );
      setPos({ top, left });
    };
    measure();
    const raf = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    let ro: ResizeObserver | null = null;
    if (panelRef.current && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(panelRef.current);
    }
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      ro?.disconnect();
    };
  }, [open]);

  if (!currentUser) return null;

  const initials = currentUser.initials ?? currentUser.name?.slice(0, 2).toUpperCase() ?? '?';
  const avatar = currentUser.avatar;

  const close = () => setOpen(false);

  // Operator-authored extraItems (header.profileMenu) take full ownership
  // of the rows above Sign out — the built-in "Profile" row only fills
  // in when the tenant hasn't configured any of their own, so a tenant
  // adding their own Profile-equivalent row doesn't end up with two.
  const rows: ProfileMenuItem[] =
    extraItems.length > 0
      ? extraItems
      : [{ label: tr('profile', locale), href: profileHref, icon: <UserIcon size={14} /> }];
  const signOutRow: ProfileMenuLinkItem = {
    label: tr('signOut', locale),
    href: signOutHref,
    icon: <SignOutIcon size={14} />,
    destructive: true,
    separatorBefore: true,
  };

  const avatarCircle = (
    <span
      style={{
        width: 38,
        height: 38,
        borderRadius: '50%',
        background: avatar ? 'transparent' : 'var(--color-brand-500, #6366f1)',
        color: 'var(--color-text-inverse, #ffffff)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '13px',
        fontWeight: 700,
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      {avatar ? (
        <img
          src={avatar}
          alt={currentUser.name ?? ''}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        initials
      )}
    </span>
  );

  const identityMeta = (
    <span style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
      {currentUser.name && (
        <span
          style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {currentUser.name}
        </span>
      )}
      {currentUser.email && (
        <span
          style={{
            display: 'block',
            fontSize: '11px',
            color: 'var(--color-text-tertiary, rgba(255,255,255,0.5))',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            marginTop: 2,
          }}
        >
          {currentUser.email}
        </span>
      )}
    </span>
  );

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={
          currentUser.name
            ? `${currentUser.name} — ${tr('account', locale)}`
            : tr('account', locale)
        }
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
        style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: avatar ? 'transparent' : 'var(--color-brand-500, #6366f1)',
          color: avatar
            ? 'transparent'
            : inverse
              ? 'var(--color-text-on-sunken, #ffffff)'
              : 'var(--color-text-inverse, #ffffff)',
          fontSize: '13px',
          fontWeight: 600,
          letterSpacing: 'var(--font-tracking-wide, 0.02em)',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          outline: 'none',
          padding: 0,
          border: inverse
            ? '1px solid var(--color-border-on-sunken, rgba(255,255,255,0.18))'
            : '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
          overflow: 'hidden',
        }}
      >
        {avatar ? (
          <img
            src={avatar}
            alt={currentUser.name ?? ''}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          initials
        )}
      </button>

      {open &&
        mounted &&
        createPortal(
          <div
            ref={panelRef}
            id={menuId}
            role="menu"
            aria-label={tr('account', locale)}
            style={{
              position: 'fixed',
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              visibility: pos ? 'visible' : 'hidden',
              width: variant === 'grid' ? 'min(92vw, 380px)' : undefined,
              minWidth: variant === 'grid' ? undefined : 240,
              background: 'var(--color-surface-card, #141414)',
              border: '1px solid var(--color-border-subtle, rgba(255,255,255,0.08))',
              borderRadius: 'var(--radius-md, 12px)',
              boxShadow: 'var(--shadow-xl, 0 24px 48px rgba(0,0,0,0.18))',
              color: 'var(--color-text-primary, #ffffff)',
              overflow: 'hidden',
              // Must exceed sonner's toast stack z-index (999999999 — see
              // sonner/dist/styles.css [data-sonner-toaster]), which shares
              // this menu's "top-right" corner: otherwise an incoming toast
              // renders on top of the open panel and swallows clicks meant
              // for its items. Keep this above 999999999 — do not "round
              // down" to something smaller, it needs to clear that exact
              // number.
              zIndex: 1_000_000_000,
              animation: 'btk-menu-in 160ms ease-out',
            }}
          >
            {/* User card at the top — clickable straight to the profile
                in the grid variant, since the grid drops the redundant
                built-in "Profile" tile in favour of this. */}
            {variant === 'grid' ? (
              <Link
                href={profileHref}
                role="menuitem"
                onClick={close}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: 'var(--space-3-5, 14px) var(--space-4, 16px)',
                  borderBottom: '1px solid var(--color-border-subtle, rgba(255,255,255,0.08))',
                  background: 'var(--color-surface-elevated, rgba(255,255,255,0.02))',
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                {avatarCircle}
                {identityMeta}
                <svg
                  aria-hidden
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{
                    color: 'var(--color-text-tertiary, rgba(255,255,255,0.4))',
                    flexShrink: 0,
                  }}
                >
                  <path d="m9 6 6 6-6 6" />
                </svg>
              </Link>
            ) : (
              <div
                style={{
                  padding: 'var(--space-3-5, 14px) var(--space-4, 16px)',
                  borderBottom: '1px solid var(--color-border-subtle, rgba(255,255,255,0.08))',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                {avatarCircle}
                {identityMeta}
              </div>
            )}

            <ProfileMenuEntries
              items={rows}
              variant={variant}
              onNavigate={close}
              trailingItem={variant === 'list' ? signOutRow : undefined}
            />
            {variant === 'grid' && (
              <div
                role="none"
                style={{
                  borderTop: '1px solid var(--color-border-subtle, rgba(255,255,255,0.08))',
                  padding: 'var(--space-1-5, 6px) 0',
                }}
              >
                <MenuRow item={signOutRow} onClick={close} />
              </div>
            )}
          </div>,
          document.body,
        )}

      <style
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes btk-menu-in {
              from { opacity: 0; transform: translateY(-4px); }
              to   { opacity: 1; transform: translateY(0); }
            }
          `,
        }}
      />
    </div>
  );
}

export interface ProfileMenuEntriesProps {
  /** Just the operator-authored rows — no built-in Profile fallback,
   *  no sign-out row (see {@code trailingItem}). */
  items: ProfileMenuItem[];
  /** Panel layout. Same meaning as {@link ProfileMenuProps.variant}. */
  variant?: 'list' | 'grid';
  /** Called after any row/tile is clicked (e.g. close a popover or drawer). */
  onNavigate?: () => void;
  /** Appended as the last row in the {@code 'list'} variant (e.g.
   *  sign-out) — its own {@code separatorBefore} controls the divider
   *  above it. The {@code 'grid'} variant ignores this; callers render
   *  it separately as a footer row instead, since a destructive action
   *  doesn't belong inside the tile grid. */
  trailingItem?: ProfileMenuLinkItem;
}

/**
 * The rows/tiles between a {@link ProfileMenu}'s identity card and its
 * sign-out footer — extracted so the mobile drawer can render the
 * exact same list/grid+groups layout for a given {@code profileMenuStyle}
 * instead of maintaining a second, parallel rendering of the same data
 * (see {@code MobileMenu} in {@code shells/headers/index.tsx}).
 */
export function ProfileMenuEntries({
  items,
  variant = 'list',
  onNavigate,
  trailingItem,
}: ProfileMenuEntriesProps) {
  const navigate = onNavigate ?? (() => {});

  if (variant === 'grid') {
    const gridSections = partitionForGrid(items);
    return (
      <div
        role="none"
        style={{
          padding: '10px 6px 4px',
          maxHeight: '56vh',
          overflowY: 'auto',
        }}
      >
        {(() => {
          // Only 'group' sections consume a palette slot — an
          // ungrouped batch ahead of the first real group shouldn't
          // shift the rotation.
          let groupIndex = -1;
          return gridSections.map((section, si) => {
            if (section.kind === 'links') {
              return (
                <div
                  key={`links-${si}`}
                  style={{
                    padding: '4px 8px 10px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(76px, 1fr))',
                    gap: 4,
                  }}
                >
                  {section.items.map((item, ii) => (
                    <GridTile key={ii} item={item} tint={UNGROUPED_TINT} onClick={navigate} />
                  ))}
                </div>
              );
            }
            groupIndex += 1;
            const { group } = section;
            const tint = tintFor(groupIndex, group.color);
            return (
              <section
                key={`group-${si}`}
                //style={{ padding: '4px 8px 10px' }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    margin: '0 4px 8px',
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 7,
                      flexShrink: 0,
                      fontSize: '10.5px',
                      fontWeight: 700,
                      letterSpacing: '0.07em',
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                      color: 'var(--color-text-tertiary, rgba(255,255,255,0.4))',
                    }}
                  >
                    {group.icon ? (
                      <span
                        aria-hidden
                        style={{
                          display: 'inline-flex',
                          color: tint,
                          flexShrink: 0,
                        }}
                      >
                        {group.icon}
                      </span>
                    ) : (
                      <span
                        aria-hidden
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          background: tint,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    {group.label}
                  </span>
                  <span
                    aria-hidden
                    style={{
                      flex: 1,
                      height: 1,
                      background: 'var(--color-border-subtle, rgba(255,255,255,0.08))',
                    }}
                  />
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(76px, 1fr))',
                    gap: 4,
                  }}
                >
                  {group.links.map((item, ii) => (
                    <GridTile key={ii} item={item} tint={tint} onClick={navigate} />
                  ))}
                </div>
              </section>
            );
          });
        })()}
      </div>
    );
  }

  const listItems = trailingItem ? [...flattenForList(items), trailingItem] : flattenForList(items);
  return (
    <div role="none" style={{ padding: 'var(--space-1-5, 6px) 0' }}>
      {listItems.map((item, i) => (
        <span key={i} role="none">
          {item.separatorBefore && (
            <div
              style={{
                height: 1,
                background: 'var(--color-border-subtle, rgba(255,255,255,0.06))',
                margin: 'var(--space-1-5, 6px) var(--space-3, 12px)',
              }}
              aria-hidden
            />
          )}
          <MenuRow item={item} onClick={navigate} />
        </span>
      ))}
    </div>
  );
}

/** One row in the {@code 'list'} variant, and the sign-out footer row
 *  in the {@code 'grid'} variant — same visual treatment either way. */
function MenuRow({ item, onClick }: { item: ProfileMenuLinkItem; onClick: () => void }) {
  return (
    <Link
      role="menuitem"
      href={item.href}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 'var(--space-2-5, 10px) var(--space-4, 16px)',
        fontSize: '13px',
        color: item.destructive
          ? 'var(--color-danger-fg, #ef4444)'
          : 'var(--color-text-primary, #ffffff)',
        textDecoration: 'none',
        transition: 'background 120ms ease',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.background =
          'var(--color-surface-elevated, rgba(255,255,255,0.04))';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
      }}
    >
      {item.icon && (
        <span
          style={{
            width: 18,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: item.destructive
              ? 'var(--color-danger-fg, #ef4444)'
              : 'var(--color-text-secondary, rgba(255,255,255,0.65))',
            flexShrink: 0,
          }}
        >
          {item.icon}
        </span>
      )}
      <span>{item.label}</span>
    </Link>
  );
}

/** One icon-over-label tile in the {@code 'grid'} variant. */
function GridTile({
  item,
  tint,
  onClick,
}: {
  item: ProfileMenuLinkItem;
  tint: string;
  onClick: () => void;
}) {
  return (
    <Link
      role="menuitem"
      href={item.href}
      onClick={onClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        padding: '12px 6px 10px',
        borderRadius: 14,
        textDecoration: 'none',
        color: 'var(--color-text-primary, #ffffff)',
        transition: 'background 120ms ease',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.background =
          `color-mix(in srgb, ${tint} 9%, transparent)`;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
      }}
    >
      <span
        aria-hidden
        style={{
          width: 38,
          height: 38,
          borderRadius: 12,
          display: 'grid',
          placeItems: 'center',
          background: `color-mix(in srgb, ${tint} 16%, var(--color-surface-card, #141414))`,
          color: tint,
          flexShrink: 0,
        }}
      >
        {item.icon}
      </span>
      <span
        style={{
          fontSize: '11.5px',
          fontWeight: 500,
          lineHeight: 1.25,
          textAlign: 'center',
          color: 'var(--color-text-secondary, rgba(255,255,255,0.65))',
        }}
      >
        {item.label}
      </span>
    </Link>
  );
}

/** Tiny built-in dictionary for the three labels the menu hard-codes.
 *  Renamed from {@code t} to {@code tr} to avoid colliding with the
 *  platform {@code t()} import — the menu's translations are a fixed
 *  set, not a per-tenant config. */
function tr(key: string, locale: string): string {
  const dict: Record<string, Record<string, string>> = {
    profile: { tr: 'Profil', en: 'Profile' },
    signOut: { tr: 'Çıkış Yap', en: 'Sign out' },
    account: { tr: 'Hesap', en: 'Account' },
  };
  return dict[key]?.[locale] ?? dict[key]?.['en'] ?? key;
}

function UserIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
    </svg>
  );
}
function SignOutIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
    </svg>
  );
}
